import { useQuery } from '@tanstack/react-query';
import xor from 'lodash/xor';

import useAuth from '@/hooks/useAuth';

import type { OperatorDto } from '@/_generated/api-clients/xp';
import { getOperatorsOptions } from '@/_generated/api-clients/xp';

// TODO: Rename to useOperators if this hook's scope remains broader than plan-specific naming suggests.
const usePlanOperators = () => {
  const { data: auth } = useAuth();
  const isAuthLoaded = !!auth?.userId;

  const { data, isFetching, isPending } = useQuery({
    ...getOperatorsOptions(),
    enabled: isAuthLoaded,
  });

  const operators = data;

  const leaderLocations = auth?.allLocationsWithAtLeastLeaderPermissions ?? [];
  const leaderCorpLocations = auth?.leaderCorpLocations ?? [];
  const tgLocations = auth?.supportCenterLocations.tgLocations ?? [];

  const leaderOperators = (operators ?? []).filter(operator => {
    const isLeaderForOperator = operator.locations?.some(loc =>
      leaderLocations.includes(loc),
    );

    if (!isLeaderForOperator) return false;
    if (!leaderCorpLocations.length) return true;

    // Exclude operators whose only overlap is via corp (Interim Manager) locations.
    // https://cfacorp.atlassian.net/browse/PATHWAY-2287
    const corpLocationIds = leaderCorpLocations.map(corp => corp.number);
    const nonCorpLocationIds = xor(corpLocationIds, leaderLocations);
    return xor(operator.locations, nonCorpLocationIds).length > 0;
  });

  const tgOperators: OperatorDto[] = tgLocations.map(loc => ({
    id: loc.id,
    name: loc.name,
  }));

  const assignableOperators: OperatorDto[] = tgOperators.length
    ? tgOperators
    : leaderOperators;

  return {
    isFetching: isFetching || isPending,
    operators: assignableOperators,
  };
};

export default usePlanOperators;
